import React, { useEffect } from 'react';
import { FormikProps } from 'formik';
import { IProductInitialValuesProps, IndetermiableBoolean } from './types';
import {
  updateProductVariantsWithOptionValues,
  updateProductVariantsWithTrackQuantityValues,
} from './utils';

/** Deep equality, so a rewrite that changes nothing is not treated as an edit. */
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Keeps the variant list in sync with two cross-cutting form changes:
 * - when the option set changes, re-derive each variant's option values;
 * - when the track-quantity toggle changes, apply it to every variant.
 *
 * Rendered as a child of `<Formik>` so it can subscribe to form state without
 * re-rendering the whole editor.
 *
 * Both effects run on mount as well as on change, and both rewrite the whole
 * variant list, so they write only when the result actually differs from what
 * is already there. An effect that writes on mount regardless turns opening a
 * product into editing it, and a save bar that is on when nothing has happened
 * is one people learn to ignore.
 */
export const ProductFormEffects: React.FC<{
  props: FormikProps<IProductInitialValuesProps>;
  trackQuantity: IndetermiableBoolean;
}> = ({ props, trackQuantity }) => {
  useEffect(() => {
    if (props.values.options && props.values.variants?.length > 0) {
      const variants = updateProductVariantsWithOptionValues(props.values.variants, props.values.options);
      if (!same(variants, props.values.variants)) {
        props.setFieldValue('variants', variants, false);
      }
    }
  }, [props.values.options]);

  useEffect(() => {
    if (trackQuantity !== 'indeterminate') {
      const variants = updateProductVariantsWithTrackQuantityValues(props.values.variants, trackQuantity);
      if (!same(variants, props.values.variants)) {
        props.setFieldValue('variants', variants, false);
      }
    }
  }, [trackQuantity]);

  return null;
};
